import { type LoaderFunctionArgs } from '@remix-run/node';
import { redirect, typedjson, useTypedLoaderData, useTypedRouteLoaderData } from 'remix-typedjson';
import { localeCookie } from '#common/i18n/i18n.server';
import { getUnifiedShopConfiguration } from '#common/models/unified-shop-settings.server.ts';
import { serializeCookieByConsent } from '#common/utils/consent.server.js';
import { setAffiliateRefCookieIfNeeded } from '#common/utils/tracking/affiliate.server.ts';
import { setFbcCookieIfNeeded } from '#common/utils/tracking/facebook.server.ts';
import { setGoogleClickIdCookiesIfNeeded } from '#common/utils/tracking/google-ads.server.ts';
import { setTtclidCookieIfNeeded } from '#common/utils/tracking/tiktok.server.ts';
import { SUPPORTED_CURRENCIES, type SupportedCurrency } from '#common/config/currencies.ts';
import { resolveCurrency } from '#common/utils/currency.server.ts';
import { translatePath } from '#common/utils/i18n.tsx';
import { MediumSelectionProvider } from '#speaker-shop/components/MediumSelectionContext.tsx';
import { CoreUnifiedShop } from '#speaker-shop/components/CoreUnifiedShopRedesign';
import { LandingPage } from '#speaker-shop/components/LandingPage';
import { ShopLayout1 } from '#speaker-shop/components/shop-layouts/Layout1.tsx';
import { ShopLayout2 } from '#speaker-shop/components/shop-layouts/Layout2.tsx';
import { type LayoutProps, type ResolvedAvailableUse, type UnifiedShopData } from '#speaker-shop/components/shop-layouts/types.ts';
import { useVoicePageRootData } from '#speaker-shop/hooks/useVoicePageRootData';
import { defaultLang } from '#speaker-shop/i18n/config.ts';
import { getLocale } from '#speaker-shop/i18n/i18n.server.ts';
import { getAvailableUses } from '#speaker-shop/models/available-use.server';
import { getSpeakerShopData } from '#speaker-shop/models/speaker-shop-data.server';
import { getUnifiedShopData } from '#speaker-shop/models/unified-shop-data.server';
import { type loader as rootLoader } from '#speaker-shop/root';
import { currentPasswordFingerprint, getUser, safeEqual, shopSessionStorage } from '#speaker-shop/session.server.ts';
import { getAudioControls } from '#speaker-shop/utils/audioControls.server.ts';
import { getSecurityContext } from '#speaker-shop/utils/audioGenerationSecurity.server.ts';
import { resolveVoiceAndLangParams } from '#speaker-shop/utils/resolve-voice';
import { getSpeakerVoicesForLanding, resolveShopContext } from '#speaker-shop/utils/shop-context.server';

export const handle = {
    i18n: [ 'common', 'hero', 'medium-selection', 'audio-generation', 'checkout', 'footer' ]
};

const layouts: Record<string, number> = {
    '845c': 1,
    '9f32': 2
};

export async function loader({ request, params }: LoaderFunctionArgs) {
    const { voiceSlug, lang } = resolveVoiceAndLangParams(params);
    const shopContext = await resolveShopContext(request.url, voiceSlug);
    if (shopContext.unifiedShopPage && voiceSlug) {
        const locale = await getLocale(await getUser(request), lang, request);
        const url = new URL(request.url);
        const headers = new Headers();
        headers.append('Set-Cookie', await serializeCookieByConsent(request, 'functionality', localeCookie, locale));
        await setAffiliateRefCookieIfNeeded(request, headers);
        if (process.env.ENV === 'prod') {
            await setTtclidCookieIfNeeded(request, headers);
            await setFbcCookieIfNeeded(request, headers);
            await setGoogleClickIdCookiesIfNeeded(request, headers);
        }
        throw redirect(`${translatePath('/', locale, { defaultLang })}${url.search}`, { headers });
    }
    if (shopContext.shopMode === 'SINGLE_SUBDOMAIN' && !voiceSlug) {
        const locale = await getLocale(await getUser(request), lang, request);
        if (shopContext.unifiedShopPage && shopContext.speakerId) {
            const configuration = await getUnifiedShopConfiguration(shopContext.speakerId);
            if (configuration.shopPassword) {
                const session = await shopSessionStorage.getSession(request.headers.get('Cookie'));
                const fingerprint = session.get('fp');
                if (
                    session.get('unlocked') !== true || typeof fingerprint !== 'string'
                    || !safeEqual(fingerprint, currentPasswordFingerprint(configuration.shopPassword))
                ) {
                    throw redirect(translatePath('/login', locale, { defaultLang }));
                }
            }
            const unified = await getUnifiedShopData(request, shopContext.speakerId, shopContext.isPreview, locale);
            if (unified) {
                return typedjson({ unified });
            }
        }
        const rawVoices = await getSpeakerVoicesForLanding(shopContext.speakerId!, shopContext.isPreview);
        const voices = rawVoices.map(voice => ({
            slug: voice.slug,
            name: voice.name,
            voiceDescription: locale === 'de' ? voice.voiceDescriptionDe : voice.voiceDescriptionEn,
            imageFileName: voice.imageFileName
        }));
        return typedjson({ voices });
    }
    const speakerShopData = await getSpeakerShopData(request.url, voiceSlug);
    if (!speakerShopData) {
        throw new Response('No speaker shop data found', { status: 404 });
    }

    const password = speakerShopData.speakerVoice.speaker.unifiedShopPage
        ? speakerShopData.speakerVoice.speaker.shopPassword
        : speakerShopData.password;
    if (password) {
        const cookie = request.headers.get('Cookie');
        const session = await shopSessionStorage.getSession(cookie);

        const unlocked = session.get('unlocked') === true;
        const fp = session.get('fp');

        if (!unlocked || typeof fp !== 'string' || !safeEqual(fp, currentPasswordFingerprint(password))) {
            const locale = await getLocale(null, lang, request);
            const loginPath = voiceSlug ? `/${voiceSlug}/login` : '/login';
            throw redirect(translatePath(loginPath, locale, { defaultLang }));
        }
    }

    const name = speakerShopData.name!;
    const shortName = name.split(' ')[0]!;

    const url = new URL(request.url);

    const securityContext = await getSecurityContext(request);
    const defaultAudioControls = await getAudioControls(request, voiceSlug);

    const variantParam = url.searchParams.get('variant');
    const layoutParam = url.searchParams.get('layout');
    const layout = variantParam ? layouts[variantParam] || 1 : layoutParam ? parseInt(layoutParam) || 1 : 1;

    const rawAvailableUses = await getAvailableUses(request.url, voiceSlug);
    const availableCurrencies = (speakerShopData.supportedCurrencies as string[]).filter((c): c is SupportedCurrency =>
        SUPPORTED_CURRENCIES.includes(c as SupportedCurrency)
    ).sort((a, b) => SUPPORTED_CURRENCIES.indexOf(a) - SUPPORTED_CURRENCIES.indexOf(b));
    const currency = resolveCurrency(request, availableCurrencies);

    const availableUses: ResolvedAvailableUse[] = rawAvailableUses.map(use => {
        const price = use.prices.find(p => p.currency === currency) ?? use.prices[0];
        return {
            ...use,
            basePrice: price?.basePrice ?? 0,
            currency: price?.currency ?? currency
        };
    });

    return typedjson({
        testimonials: speakerShopData.testimonials,
        shortName,
        availableUses,
        currency,
        availableCurrencies,
        ...securityContext,
        defaultAudioControls,
        layout
    });
}

export default function Index() {
    const data = useTypedLoaderData<typeof loader>();
    const rootData = useTypedRouteLoaderData<typeof rootLoader>('root')!;
    if ('unified' in data) {
        return <CoreUnifiedShop data={data.unified as UnifiedShopData} baseUrl={rootData.baseUrl} />;
    }
    if ('voices' in data) {
        return (
            <LandingPage
                voices={data.voices}
                baseUrl={rootData.baseUrl}
                speakerSlug={rootData.speakerSlug}
                speakerDisplayName={rootData.speakerDisplayName}
            />
        );
    }
    return <VoiceIndex data={data} />;
}

function VoiceIndex(
    { data }: { data: Extract<ReturnType<typeof useTypedLoaderData<typeof loader>>, { shortName: string }> },
) {
    const rootData = useVoicePageRootData();
    const { shortName, layout, testimonials, ...loadedData } = data;
    const contactName = rootData.speakerDisplayName ? rootData.speakerDisplayName.split(' ')[0]! : shortName;

    const opts: LayoutProps = {
        shortName: contactName,
        availableUses: loadedData.availableUses,
        currency: loadedData.currency,
        availableCurrencies: loadedData.availableCurrencies,
        testimonials,
        hasConsent: loadedData.hasConsent,
        isLoggedIn: loadedData.isLoggedIn,
        anonymousId: loadedData.anonymousId,
        limits: loadedData.limits,
        defaultAudioControls: loadedData.defaultAudioControls.voiceSettings!
    };

    return (
        <MediumSelectionProvider
            fallbackSelection={opts.availableUses.length ? [ opts.availableUses[0]!.id ] : []}
            availableMediums={opts.availableUses.map(x => x.id)}
        >
            {/* Always show Layout2 on mobile; on md+ show the chosen layout */}
            <div className='md:hidden'>
                <ShopLayout2 {...opts} />
            </div>
            <div className='hidden md:block'>
                {layout === 2 ? <ShopLayout2 {...opts} /> : <ShopLayout1 {...opts} />}
            </div>
        </MediumSelectionProvider>
    );
}
